"use client";

export default function MaintenancePage() {
  return (
    <div
      className="min-h-screen flex items-center justify-center p-6 relative overflow-hidden"
      style={{ background: "color-mix(in srgb, var(--background) 94%, var(--brand))" }}
    >
      <style>{`
        @keyframes icon-float {
          0%,100% { transform: translateY(0px); opacity: 0.14; }
          50%      { transform: translateY(-14px); opacity: 0.22; }
        }
        @keyframes icon-float2 {
          0%,100% { transform: translateY(0px) rotate(0deg); opacity: 0.12; }
          50%      { transform: translateY(-18px) rotate(4deg); opacity: 0.2; }
        }
        @keyframes icon-float3 {
          0%,100% { transform: translateY(0px) rotate(0deg); opacity: 0.1; }
          40%      { transform: translateY(12px) rotate(-3deg); opacity: 0.18; }
        }
        @keyframes gear-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes gear-spin-rev {
          from { transform: rotate(0deg); }
          to   { transform: rotate(-360deg); }
        }
        @keyframes screen-blink {
          0%,100% { opacity: 1; }
          48%,52% { opacity: 0.4; }
        }
        @keyframes bar-scan {
          0%,100% { transform: translateY(0px); opacity: 0.8; }
          50%      { transform: translateY(18px); opacity: 0.3; }
        }
        @keyframes dot-pulse {
          0%,100% { opacity: 0.3; transform: scale(0.8); }
          50%      { opacity: 1; transform: scale(1); }
        }
        @keyframes card-slide {
          0%,100% { transform: translateX(0px); }
          50%      { transform: translateX(6px); }
        }
        @keyframes receipt-grow {
          0%,100% { transform: scaleY(1); }
          50%      { transform: scaleY(1.04); }
        }
        @keyframes grid-drift {
          0%   { background-position: 0 0; }
          100% { background-position: 40px 40px; }
        }
        .fi1  { animation: icon-float  9s ease-in-out 0s infinite; }
        .fi2  { animation: icon-float2 11s ease-in-out 1s infinite; }
        .fi3  { animation: icon-float3 13s ease-in-out 2s infinite; }
        .fi4  { animation: icon-float  10s ease-in-out 3s infinite reverse; }
        .fi5  { animation: icon-float2 12s ease-in-out 4s infinite reverse; }
        .fi6  { animation: icon-float3 8s ease-in-out 1.5s infinite; }
        .fi7  { animation: icon-float  14s ease-in-out 5s infinite; }
        .fi8  { animation: icon-float2 9s ease-in-out 2.5s infinite reverse; }
        .g1   { animation: gear-spin     7s linear infinite; transform-box: fill-box; transform-origin: center; }
        .g2   { animation: gear-spin-rev 5s linear infinite; transform-box: fill-box; transform-origin: center; }
        .g3   { animation: gear-spin     9s linear 1s infinite; transform-box: fill-box; transform-origin: center; }
        .scr  { animation: screen-blink 4s ease-in-out infinite; }
        .scan { animation: bar-scan 2.4s ease-in-out infinite; }
        .crd  { animation: card-slide 3s ease-in-out infinite; }
        .rct  { animation: receipt-grow 3.5s ease-in-out infinite; transform-origin: bottom; }
        .dp1  { animation: dot-pulse 1.4s ease-in-out 0s infinite; }
        .dp2  { animation: dot-pulse 1.4s ease-in-out 0.46s infinite; }
        .dp3  { animation: dot-pulse 1.4s ease-in-out 0.92s infinite; }
        .grid-bg { animation: grid-drift 6s linear infinite; }
      `}</style>

      {/* ── Subtle dot grid background ── */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle, color-mix(in srgb, var(--brand) 18%, transparent) 1px, transparent 1px)`,
          backgroundSize: "32px 32px",
          opacity: 0.5,
          zIndex: 0,
        }}
      />

      {/* ── Floating POS-themed background icons ── */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 1 }}>

        {/* TOP-LEFT: POS terminal screen */}
        <div className="fi1 absolute" style={{ top: "6%", left: "4%" }}>
          <svg viewBox="0 0 96 80" width="88" height="72" fill="none">
            <rect x="4" y="4" width="88" height="56" rx="8" fill="var(--brand)" opacity="0.15" stroke="var(--brand)" strokeWidth="1.5" strokeOpacity="0.3"/>
            <rect x="10" y="10" width="76" height="44" rx="5" fill="var(--brand)" opacity="0.08"/>
            <line x1="18" y1="22" x2="48" y2="22" stroke="var(--brand)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.4"/>
            <line x1="18" y1="30" x2="60" y2="30" stroke="var(--brand)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.3"/>
            <line x1="18" y1="38" x2="40" y2="38" stroke="var(--brand)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.25"/>
            <rect x="36" y="60" width="24" height="8" rx="2" fill="var(--brand)" opacity="0.2"/>
            <rect x="20" y="68" width="56" height="6" rx="2" fill="var(--brand)" opacity="0.15"/>
          </svg>
        </div>

        {/* TOP-RIGHT: Credit card */}
        <div className="fi2 absolute" style={{ top: "5%", right: "5%" }}>
          <svg viewBox="0 0 100 68" width="90" height="62" fill="none">
            <rect x="2" y="2" width="96" height="64" rx="10" fill="var(--brand)" opacity="0.12" stroke="var(--brand)" strokeWidth="1.5" strokeOpacity="0.28"/>
            <rect x="2" y="16" width="96" height="14" fill="var(--brand)" opacity="0.1"/>
            <rect x="10" y="38" width="20" height="14" rx="3" fill="var(--brand)" opacity="0.2"/>
            <line x1="40" y1="42" x2="72" y2="42" stroke="var(--brand)" strokeWidth="1.8" strokeLinecap="round" strokeOpacity="0.3"/>
            <line x1="40" y1="48" x2="60" y2="48" stroke="var(--brand)" strokeWidth="1.8" strokeLinecap="round" strokeOpacity="0.22"/>
          </svg>
        </div>

        {/* LEFT-MIDDLE: Barcode */}
        <div className="fi3 absolute" style={{ top: "38%", left: "2%" }}>
          <svg viewBox="0 0 70 50" width="62" height="44" fill="none">
            <rect x="2" y="2" width="66" height="46" rx="5" fill="var(--brand)" opacity="0.07" stroke="var(--brand)" strokeWidth="1" strokeOpacity="0.2"/>
            {[8,13,16,20,24,28,31,36,40,44,47,52,56,60].map((x, i) => (
              <rect key={i} x={x} y="8" width={i % 3 === 0 ? 2.5 : 1.5} height="28" rx="0.5" fill="var(--brand)" opacity="0.28"/>
            ))}
            <line x1="8" y1="40" x2="62" y2="40" stroke="var(--brand)" strokeWidth="1" strokeOpacity="0.2" strokeLinecap="round"/>
          </svg>
        </div>

        {/* RIGHT-MIDDLE: Receipt */}
        <div className="fi4 absolute" style={{ top: "36%", right: "2.5%" }}>
          <svg viewBox="0 0 56 88" width="48" height="76" fill="none">
            <path d="M4 4 H52 V80 L44 72 L36 80 L28 72 L20 80 L12 72 L4 80 Z" fill="var(--brand)" opacity="0.1" stroke="var(--brand)" strokeWidth="1.5" strokeOpacity="0.25" strokeLinejoin="round"/>
            <line x1="12" y1="20" x2="44" y2="20" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.3"/>
            <line x1="12" y1="30" x2="44" y2="30" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.22"/>
            <line x1="12" y1="40" x2="30" y2="40" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.18"/>
            <line x1="12" y1="50" x2="44" y2="50" stroke="var(--brand)" strokeWidth="1.8" strokeLinecap="round" strokeOpacity="0.28"/>
            <line x1="12" y1="60" x2="36" y2="60" stroke="var(--brand)" strokeWidth="1.2" strokeLinecap="round" strokeOpacity="0.18"/>
          </svg>
        </div>

        {/* BOTTOM-LEFT: Coin stack */}
        <div className="fi5 absolute" style={{ bottom: "6%", left: "5%" }}>
          <svg viewBox="0 0 70 72" width="60" height="62" fill="none">
            {[48, 38, 28, 18].map((y, i) => (
              <g key={i}>
                <ellipse cx="35" cy={y + 8} rx="28" ry="8" fill="var(--brand)" opacity={0.08 + i * 0.03}/>
                <ellipse cx="35" cy={y} rx="28" ry="8" fill="var(--brand)" opacity={0.12 + i * 0.03} stroke="var(--brand)" strokeWidth="1" strokeOpacity="0.25"/>
              </g>
            ))}
          </svg>
        </div>

        {/* BOTTOM-RIGHT: Shopping bag */}
        <div className="fi6 absolute" style={{ bottom: "5%", right: "4%" }}>
          <svg viewBox="0 0 72 82" width="62" height="70" fill="none">
            <path d="M12 28 H60 L54 76 H18 Z" fill="var(--brand)" opacity="0.12" stroke="var(--brand)" strokeWidth="1.5" strokeOpacity="0.28" strokeLinejoin="round"/>
            <path d="M26 28 C26 20 46 20 46 28" stroke="var(--brand)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.35" fill="none"/>
            <line x1="22" y1="46" x2="50" y2="46" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.25"/>
          </svg>
        </div>

        {/* TOP-CENTER: Analytics chart */}
        <div className="fi7 absolute" style={{ top: "4%", left: "42%" }}>
          <svg viewBox="0 0 80 60" width="68" height="52" fill="none">
            <rect x="2" y="2" width="76" height="56" rx="7" fill="var(--brand)" opacity="0.08" stroke="var(--brand)" strokeWidth="1" strokeOpacity="0.2"/>
            <polyline points="10,44 24,28 38,34 52,16 70,22"
              stroke="var(--brand)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.35" fill="none"/>
            <circle cx="10" cy="44" r="2.5" fill="var(--brand)" opacity="0.35"/>
            <circle cx="24" cy="28" r="2.5" fill="var(--brand)" opacity="0.35"/>
            <circle cx="38" cy="34" r="2.5" fill="var(--brand)" opacity="0.35"/>
            <circle cx="52" cy="16" r="2.5" fill="var(--brand)" opacity="0.35"/>
            <circle cx="70" cy="22" r="2.5" fill="var(--brand)" opacity="0.35"/>
          </svg>
        </div>

        {/* TOP-LEFT area: Wrench */}
        <div className="fi2 absolute" style={{ top: "28%", left: "3.5%" }}>
          <svg viewBox="0 0 64 64" width="52" height="52" fill="none">
            <path d="M44 8 C38 8 33 13 33 19 C33 21.5 33.8 23.8 35.2 25.6 L10 50 C8.3 51.7 8.3 54.4 10 56.1 C11.7 57.8 14.4 57.8 16.1 56.1 L41.4 30.8 C43.2 32.2 45.5 33 48 33 C54 33 59 28 59 22 C59 20.1 58.5 18.3 57.6 16.7 L50 24 L44 24 L40 20 L40 14 L47.3 6.4 C46.2 6.1 45.1 6 44 6Z"
              fill="color-mix(in srgb,var(--brand) 18%,transparent)" stroke="color-mix(in srgb,var(--brand) 38%,transparent)" strokeWidth="1.5" strokeLinejoin="round"/>
          </svg>
        </div>

        {/* RIGHT area: Screwdriver */}
        <div className="fi5 absolute" style={{ top: "20%", right: "3%" }}>
          <svg viewBox="0 0 52 80" width="40" height="62" fill="none">
            <rect x="22" y="4" width="8" height="36" rx="4"
              fill="color-mix(in srgb,var(--brand) 20%,transparent)" stroke="color-mix(in srgb,var(--brand) 38%,transparent)" strokeWidth="1.4"/>
            <rect x="18" y="4" width="16" height="12" rx="4"
              fill="color-mix(in srgb,var(--brand) 28%,transparent)"/>
            <rect x="24" y="38" width="4" height="28" rx="2"
              fill="color-mix(in srgb,var(--brand) 32%,transparent)" stroke="color-mix(in srgb,var(--brand) 40%,transparent)" strokeWidth="1.2"/>
            <path d="M22 66 L26 76 L30 66 Z"
              fill="color-mix(in srgb,var(--brand) 35%,transparent)"/>
          </svg>
        </div>

        {/* BOTTOM-LEFT area: Traffic cone */}
        <div className="fi6 absolute" style={{ bottom: "20%", left: "7%" }}>
          <svg viewBox="0 0 56 72" width="46" height="60" fill="none">
            <path d="M28 6 L46 62 H10 Z"
              fill="color-mix(in srgb,var(--warning) 22%,transparent)" stroke="color-mix(in srgb,var(--warning) 40%,transparent)" strokeWidth="1.5" strokeLinejoin="round"/>
            <line x1="17" y1="32" x2="39" y2="32" stroke="color-mix(in srgb,var(--warning) 45%,transparent)" strokeWidth="3" strokeLinecap="round"/>
            <line x1="13" y1="46" x2="43" y2="46" stroke="color-mix(in srgb,var(--warning) 45%,transparent)" strokeWidth="3" strokeLinecap="round"/>
            <rect x="6" y="62" width="44" height="7" rx="3"
              fill="color-mix(in srgb,var(--warning) 25%,transparent)" stroke="color-mix(in srgb,var(--warning) 38%,transparent)" strokeWidth="1.2"/>
          </svg>
        </div>

        {/* BOTTOM-RIGHT area: Hard hat */}
        <div className="fi3 absolute" style={{ bottom: "22%", right: "6%" }}>
          <svg viewBox="0 0 72 56" width="60" height="48" fill="none">
            <path d="M36 6 C20 6 8 18 8 32 H64 C64 18 52 6 36 6Z"
              fill="color-mix(in srgb,var(--warning) 20%,transparent)" stroke="color-mix(in srgb,var(--warning) 38%,transparent)" strokeWidth="1.5"/>
            <rect x="4" y="30" width="64" height="10" rx="5"
              fill="color-mix(in srgb,var(--warning) 25%,transparent)" stroke="color-mix(in srgb,var(--warning) 38%,transparent)" strokeWidth="1.2"/>
            <line x1="36" y1="8" x2="36" y2="30" stroke="color-mix(in srgb,var(--warning) 38%,transparent)" strokeWidth="1.8" strokeLinecap="round" strokeDasharray="3 3"/>
          </svg>
        </div>

        {/* BOTTOM-CENTER: Barcode scanner beam */}
        <div className="fi8 absolute" style={{ bottom: "7%", left: "40%" }}>
          <svg viewBox="0 0 68 44" width="58" height="38" fill="none">
            <rect x="2" y="2" width="64" height="40" rx="6" fill="var(--brand)" opacity="0.07" stroke="var(--brand)" strokeWidth="1" strokeOpacity="0.18"/>
            <line x1="10" y1="12" x2="10" y2="32" stroke="var(--brand)" strokeWidth="2.5" strokeLinecap="round" strokeOpacity="0.28"/>
            <line x1="16" y1="10" x2="16" y2="34" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.22"/>
            <line x1="21" y1="12" x2="21" y2="32" stroke="var(--brand)" strokeWidth="3" strokeLinecap="round" strokeOpacity="0.25"/>
            <line x1="27" y1="10" x2="27" y2="34" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.2"/>
            <line x1="32" y1="12" x2="32" y2="32" stroke="var(--brand)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.28"/>
            <line x1="37" y1="10" x2="37" y2="34" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.22"/>
            <line x1="42" y1="12" x2="42" y2="32" stroke="var(--brand)" strokeWidth="3" strokeLinecap="round" strokeOpacity="0.25"/>
            <line x1="48" y1="10" x2="48" y2="34" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.2"/>
            <line x1="53" y1="12" x2="53" y2="32" stroke="var(--brand)" strokeWidth="2" strokeLinecap="round" strokeOpacity="0.28"/>
            <line x1="58" y1="10" x2="58" y2="34" stroke="var(--brand)" strokeWidth="1.5" strokeLinecap="round" strokeOpacity="0.22"/>
          </svg>
        </div>
      </div>

      {/* ── Main card ── */}
      <div
        className="relative w-full max-w-md rounded-[28px] overflow-hidden text-center px-8 py-12"
        style={{
          zIndex: 2,
          background: "color-mix(in srgb, var(--card) 90%, transparent)",
          border: "1px solid color-mix(in srgb, var(--brand) 20%, var(--border))",
          backdropFilter: "blur(28px)",
          WebkitBackdropFilter: "blur(28px)",
          boxShadow: "0 24px 64px rgba(0,0,0,0.14), 0 1px 0 rgba(255,255,255,0.07) inset",
        }}
      >
        {/* ── Central POS terminal illustration ── */}
        <div className="relative flex items-center justify-center mb-8" style={{ height: "160px" }}>

          {/* Gear top-left */}
          <svg className="g1 absolute" style={{ left: "10%", top: "8%" }} viewBox="0 0 40 40" width="36" height="36" fill="none">
            <circle cx="20" cy="20" r="7" stroke="color-mix(in srgb,var(--brand) 50%,transparent)" strokeWidth="2"/>
            <path d="M20 4v4M20 32v4M4 20h4M32 20h4M7.5 7.5l2.8 2.8M29.7 29.7l2.8 2.8M7.5 32.5l2.8-2.8M29.7 10.3l2.8-2.8"
              stroke="color-mix(in srgb,var(--brand) 50%,transparent)" strokeWidth="2.2" strokeLinecap="round"/>
            <circle cx="20" cy="20" r="3.5" fill="color-mix(in srgb,var(--brand) 25%,transparent)"/>
          </svg>

          {/* Gear bottom-right */}
          <svg className="g2 absolute" style={{ right: "12%", bottom: "6%" }} viewBox="0 0 32 32" width="30" height="30" fill="none">
            <circle cx="16" cy="16" r="5.5" stroke="color-mix(in srgb,var(--brand) 45%,transparent)" strokeWidth="1.8"/>
            <path d="M16 3v3M16 26v3M3 16h3M26 16h3M6.2 6.2l2.1 2.1M23.7 23.7l2.1 2.1M6.2 25.8l2.1-2.1M23.7 8.3l2.1-2.1"
              stroke="color-mix(in srgb,var(--brand) 45%,transparent)" strokeWidth="2" strokeLinecap="round"/>
            <circle cx="16" cy="16" r="2.8" fill="color-mix(in srgb,var(--brand) 22%,transparent)"/>
          </svg>

          {/* Gear top-right small */}
          <svg className="g3 absolute" style={{ right: "18%", top: "10%" }} viewBox="0 0 24 24" width="22" height="22" fill="none">
            <circle cx="12" cy="12" r="4" stroke="color-mix(in srgb,var(--brand) 38%,transparent)" strokeWidth="1.6"/>
            <path d="M12 2v2.5M12 19.5V22M2 12h2.5M19.5 12H22M4.9 4.9l1.8 1.8M17.3 17.3l1.8 1.8M4.9 19.1l1.8-1.8M17.3 6.7l1.8-1.8"
              stroke="color-mix(in srgb,var(--brand) 38%,transparent)" strokeWidth="1.8" strokeLinecap="round"/>
          </svg>

          {/* POS Terminal SVG */}
          <svg viewBox="0 0 140 160" width="130" height="148" fill="none" className="relative">
            {/* Stand base */}
            <rect x="44" y="138" width="52" height="8" rx="4"
              fill="color-mix(in srgb,var(--brand) 22%,var(--card))"
              stroke="color-mix(in srgb,var(--brand) 35%,transparent)" strokeWidth="1.2"/>
            {/* Stand neck */}
            <rect x="62" y="122" width="16" height="18" rx="3"
              fill="color-mix(in srgb,var(--brand) 18%,var(--card))"
              stroke="color-mix(in srgb,var(--brand) 30%,transparent)" strokeWidth="1.2"/>

            {/* Monitor body */}
            <rect x="8" y="8" width="124" height="116" rx="12"
              fill="color-mix(in srgb,var(--brand) 14%,var(--card))"
              stroke="color-mix(in srgb,var(--brand) 38%,transparent)" strokeWidth="1.5"/>

            {/* Screen */}
            <rect className="scr" x="16" y="16" width="108" height="86" rx="7"
              fill="color-mix(in srgb,var(--brand) 10%,var(--surface))"
              stroke="color-mix(in srgb,var(--brand) 28%,transparent)" strokeWidth="1"/>

            {/* Screen content: chart bars */}
            <rect x="28" y="64" width="12" height="28" rx="3" fill="color-mix(in srgb,var(--brand) 35%,transparent)"/>
            <rect x="46" y="48" width="12" height="44" rx="3" fill="color-mix(in srgb,var(--brand) 45%,transparent)"/>
            <rect x="64" y="56" width="12" height="36" rx="3" fill="color-mix(in srgb,var(--brand) 38%,transparent)"/>
            <rect x="82" y="36" width="12" height="56" rx="3" fill="color-mix(in srgb,var(--brand) 55%,transparent)"/>
            <rect x="100" y="52" width="12" height="40" rx="3" fill="color-mix(in srgb,var(--brand) 42%,transparent)"/>

            {/* Scanner line on screen */}
            <line className="scan" x1="24" y1="60" x2="116" y2="60"
              stroke="color-mix(in srgb,var(--brand) 70%,transparent)" strokeWidth="1.5" strokeLinecap="round" strokeDasharray="4 3"/>

            {/* Bottom bar below screen */}
            <rect x="16" y="108" width="108" height="10" rx="4"
              fill="color-mix(in srgb,var(--brand) 16%,transparent)"/>
            <circle cx="70" cy="113" r="2.5" fill="color-mix(in srgb,var(--brand) 40%,transparent)"/>

            {/* Card reader slot */}
            <rect x="52" y="118" width="36" height="6" rx="3"
              fill="color-mix(in srgb,var(--brand) 20%,transparent)"
              stroke="color-mix(in srgb,var(--brand) 32%,transparent)" strokeWidth="1"/>
          </svg>

          {/* Floating credit card sliding into reader */}
          <div className="crd absolute" style={{ bottom: "0px", left: "calc(50% - 36px)" }}>
            <svg viewBox="0 0 72 20" width="64" height="18" fill="none">
              <rect x="1" y="1" width="70" height="18" rx="4"
                fill="color-mix(in srgb,var(--brand) 28%,var(--card))"
                stroke="color-mix(in srgb,var(--brand) 50%,transparent)" strokeWidth="1"/>
              <rect x="6" y="5" width="14" height="10" rx="2"
                fill="color-mix(in srgb,var(--brand) 40%,transparent)"/>
              <line x1="26" y1="8" x2="56" y2="8" stroke="color-mix(in srgb,var(--brand) 35%,transparent)" strokeWidth="1.2" strokeLinecap="round"/>
              <line x1="26" y1="13" x2="44" y2="13" stroke="color-mix(in srgb,var(--brand) 28%,transparent)" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
          </div>
        </div>

        {/* Badge */}
        <div
          className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-bold mb-4"
          style={{
            background: "color-mix(in srgb,var(--brand) 10%,var(--surface))",
            color: "var(--brand)",
            border: "1px solid color-mix(in srgb,var(--brand) 26%,transparent)",
          }}
        >
          <span className="dp1 h-1.5 w-1.5 rounded-full inline-block" style={{ background: "var(--brand)" }} />
          System Maintenance
        </div>

        <h1 className="text-3xl sm:text-[34px] font-extrabold mb-3 leading-tight" style={{ color: "var(--foreground)" }}>
          We&rsquo;ll be back<br />very soon
        </h1>

        <p className="text-sm leading-relaxed mb-3 max-w-xs mx-auto" style={{ color: "var(--muted)" }}>
          This system is currently under maintenance.
        </p>

        {/* Animated dots */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <span className="dp1 h-2 w-2 rounded-full inline-block" style={{ background: "var(--brand)" }} />
          <span className="dp2 h-2 w-2 rounded-full inline-block" style={{ background: "var(--brand)" }} />
          <span className="dp3 h-2 w-2 rounded-full inline-block" style={{ background: "var(--brand)" }} />
        </div>

        {/* Divider */}
        <div className="h-px mx-6 mb-8" style={{ background: "color-mix(in srgb,var(--brand) 16%,var(--border))" }} />

        {/* Contact */}
        <p className="text-xs mb-3 font-semibold uppercase tracking-widest" style={{ color: "var(--muted)" }}>
          Questions or enquiries?
        </p>
        <a
          href="mailto:contact@sale-pos.co.uk"
          className="inline-flex items-center gap-2.5 rounded-2xl px-5 py-3 text-sm font-semibold transition-all duration-100 active:scale-95 hover:opacity-90"
          style={{
            background: "color-mix(in srgb,var(--brand) 10%,var(--surface))",
            color: "var(--brand)",
            border: "1px solid color-mix(in srgb,var(--brand) 28%,transparent)",
          }}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4 shrink-0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
          contact@sale-pos.co.uk
        </a>
      </div>
    </div>
  );
}
